import {
  ZadMaarefatiFollowupUser,
  ZadUnpaidFollowupDossier,
  ZadUnpaidFollowupMe,
  ZadUnpaidFollowupSummary,
  ZadUnpaidSaleFinancial,
} from '../../../models/zad'
import {ZadApiError} from './errors'
import {formatZadUser} from './stockHelpers'

export const ZAD_UNPAID_FOLLOWUP_MANAGE = 'ZAD_UNPAID_FOLLOWUP_MANAGE'
export const UNPAID_FOLLOWUP_FORBIDDEN = 'UNPAID_FOLLOWUP_FORBIDDEN'
export const CANDIDATE_SCAN_CAP_DEFAULT = 2000

export type ZadUnpaidHistoryTab = 'history' | 'unpaid'
export type ZadUnpaidSubview = 'queue' | 'dossiers' | 'promises' | 'reminders' | 'settled' | 'archives'

export function canAdminZadUnpaidFollowupPermissions(role?: string | number | null): boolean {
  return String(role ?? '') === '2'
}

export function extractZadUnpaidFollowupMe(data: any): ZadUnpaidFollowupMe | null {
  if (!data || data.success === false) return null
  const role = data.role != null ? String(data.role) : null
  const alwaysAllowed = data.alwaysAllowed === true || role === '2'
  const canManage = alwaysAllowed || data.canManage === true || data.enabled === true
  const canRead = alwaysAllowed || canManage || data.canRead === true || role === '6'
  return {
    permissionKey: data.permissionKey || ZAD_UNPAID_FOLLOWUP_MANAGE,
    enabled: canManage,
    canRead,
    canManage,
    role,
    alwaysAllowed,
  }
}

export function isZadUnpaidFollowupReadable(
  me: ZadUnpaidFollowupMe | null,
  options?: {loading?: boolean; failed?: boolean}
): boolean {
  if (options?.loading || options?.failed) return false
  if (!me) return false
  if (me.alwaysAllowed === true) return true
  return me.canRead === true || me.canManage === true
}

export function isZadUnpaidFollowupWritable(
  me: ZadUnpaidFollowupMe | null,
  options?: {loading?: boolean; failed?: boolean}
): boolean {
  if (options?.loading || options?.failed) return false
  if (!me) return false
  if (me.alwaysAllowed === true) return true
  return me.canManage === true
}

export function isZadUnpaidFollowupForbidden(err: {errorCode?: string | null; status?: number | null}): boolean {
  return err.errorCode === UNPAID_FOLLOWUP_FORBIDDEN
}

export function extractZadUnpaidFollowupSummary(data: any): ZadUnpaidFollowupSummary | null {
  if (!data || data.success === false) return null
  return {
    timezone: data.timezone || 'Africa/Tunis',
    businessDate: data.businessDate || '',
    openFollowups: Number(data.openFollowups) || 0,
    outstandingLinkedSales: Number(data.outstandingLinkedSales) || 0,
    unlinkedAdministrativeDossiers: Number(data.unlinkedAdministrativeDossiers) || 0,
    unassignedUnpaidSales: Number(data.unassignedUnpaidSales) || 0,
    remindersDueToday: Number(data.remindersDueToday) || 0,
    overdueReminders: Number(data.overdueReminders) || 0,
    missedPaymentPromises: Number(data.missedPaymentPromises) || 0,
    settledButOpenDossiers: Number(data.settledButOpenDossiers) || 0,
    closedDossiersNeedingReview: Number(data.closedDossiersNeedingReview) || 0,
  }
}

export function extractZadUnpaidDossier(data: any): ZadUnpaidFollowupDossier | null {
  const raw = data?.dossier || (data?.dossierNumber ? data : null)
  if (!raw || !raw._id) return null
  return raw as ZadUnpaidFollowupDossier
}

export function extractZadMaarefatiUsers(data: any): ZadMaarefatiFollowupUser[] {
  const rows = Array.isArray(data?.users) ? data.users : []
  return rows.map((row: any) => ({
    userId: String(row.userId || row._id || ''),
    displayName: row.displayName || null,
    firstName: row.firstName || '',
    lastName: row.lastName || '',
    phone: row.phone || null,
    email: row.email || null,
    address: row.address || null,
    profileType: row.profileType || 'OTHER',
  }))
}

export function buildZadUnpaidQuery(params: Record<string, string | number | undefined | null>): string {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    query.set(key, String(value))
  })
  return query.toString()
}

export function candidateScanIncomplete(scanned?: number, scanCap?: number): boolean {
  const cap = scanCap || CANDIDATE_SCAN_CAP_DEFAULT
  return typeof scanned === 'number' && scanned >= cap
}

export function isSettledOpenDossier(dossier: ZadUnpaidFollowupDossier): boolean {
  return (
    dossier.followupStatus !== 'CLOSED' &&
    dossier.countsAsConfirmedDebt === true &&
    Number(dossier.confirmedOutstanding) <= 0
  )
}

export function isAdministrativeUnlinked(dossier: ZadUnpaidFollowupDossier): boolean {
  return dossier.customerKind === 'UNLINKED' && !(dossier.linkedSaleFinancials || []).length
}

export function dossierDisplayName(dossier: ZadUnpaidFollowupDossier): string {
  return (dossier.customerSnapshot?.displayName || '').trim() || '—'
}

export function dossierPhone(dossier: ZadUnpaidFollowupDossier): string {
  return (dossier.customerSnapshot?.phone || '').trim() || '—'
}

export function formatPromiseEvaluatedStatus(status?: string | null): string {
  return String(status || '').trim() || 'ACTIVE'
}

export function promiseStatusI18n(status?: string | null): string {
  const value = formatPromiseEvaluatedStatus(status)
  if (value === 'FULFILLED' || value === 'FULFILLED_BY_PAYMENTS') return 'ZAD.UNPAID.PROMISE_KEPT'
  if (value === 'MISSED') return 'ZAD.UNPAID.PROMISE_MISSED'
  if (value === 'CANCELLED') return 'ZAD.UNPAID.PROMISE_CANCELLED'
  return 'ZAD.UNPAID.PROMISE_ACTIVE'
}

export function followupStatusI18n(status?: string | null): string {
  const map: Record<string, string> = {
    TO_CONTACT: 'ZAD.UNPAID.STATUS_TO_CONTACT',
    IN_PROGRESS: 'ZAD.UNPAID.STATUS_IN_PROGRESS',
    PROMISE_RECEIVED: 'ZAD.UNPAID.STATUS_PROMISE',
    CONTACTED: 'ZAD.UNPAID.STATUS_CONTACTED',
    CLOSED: 'ZAD.UNPAID.STATUS_CLOSED',
  }
  return map[String(status || '')] || 'ZAD.UNPAID.STATUS_TO_CONTACT'
}

export function financialStatusI18n(status?: string | null): string {
  const map: Record<string, string> = {
    UNPAID: 'ZAD.UNPAID.FIN_UNPAID',
    PARTIAL: 'ZAD.UNPAID.FIN_PARTIAL',
    SETTLED: 'ZAD.UNPAID.FIN_SETTLED',
    CREDIT: 'ZAD.UNPAID.FIN_CREDIT',
  }
  return map[String(status || '')] || 'ZAD.UNPAID.FIN_UNPAID'
}

export function channelI18n(channel?: string | null): string {
  const map: Record<string, string> = {
    PHONE_CALL: 'ZAD.UNPAID.CHANNEL_CALL',
    WHATSAPP: 'ZAD.UNPAID.CHANNEL_WHATSAPP',
    IN_PERSON: 'ZAD.UNPAID.CHANNEL_VISIT',
    SMS: 'ZAD.UNPAID.CHANNEL_SMS',
    OTHER: 'ZAD.UNPAID.CHANNEL_OTHER',
  }
  return map[String(channel || '')] || 'ZAD.UNPAID.CHANNEL_OTHER'
}

export function whatsappHref(phone?: string | null, text?: string): string | null {
  const digits = String(phone || '').replace(/[^\d+]/g, '')
  if (!digits) return null
  const msg = text ? `?text=${encodeURIComponent(text)}` : ''
  return `https://wa.me/${digits.replace(/^\+/, '')}${msg}`
}

export function assigneeLabel(user: unknown): string {
  return formatZadUser(user as any)
}

export function saleHasPositiveOutstanding(sale?: {settlement?: unknown} | null): boolean {
  const raw = sale?.settlement
  if (!raw || typeof raw === 'string') return false
  const remaining = Number((raw as {remainingAmount?: number}).remainingAmount)
  return Number.isFinite(remaining) && remaining > 0
}

export function extractZadUnpaidTicket(data: any): ZadUnpaidSaleFinancial | null {
  const raw = data?.sale || data?.ticket || data?.candidate || (data?.saleId ? data : null)
  if (!raw || !(raw.saleId || raw._id)) return null
  return {
    ...raw,
    saleId: String(raw.saleId || raw._id),
  }
}

export const ZAD_SALES_HISTORY_NAV_KEYS = ['tab', 'followup', 'dossier', 'saleId'] as const

export function preserveZadSalesHistoryNavParams(next: URLSearchParams, current?: URLSearchParams | null): URLSearchParams {
  if (!current) return next
  ZAD_SALES_HISTORY_NAV_KEYS.forEach((key) => {
    const value = current.get(key)
    if (value) next.set(key, value)
  })
  return next
}

export function resolveZadUnpaidFollowupMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    UNPAID_FOLLOWUP_FORBIDDEN: 'ZAD.UNPAID.FORBIDDEN',
    SALE_ALREADY_FOLLOWED: 'ZAD.UNPAID.SALE_ALREADY_FOLLOWED',
    SALE_NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    SALE_NOT_VALIDATED: 'ZAD.UNPAID.SALE_NOT_VALIDATED',
    FOLLOWUP_NOT_FOUND: 'ZAD.UNPAID.NOT_FOUND',
    FOLLOWUP_CLOSED: 'ZAD.UNPAID.CLOSED',
    REMINDER_NOT_FOUND: 'ZAD.UNPAID.REMINDER_NOT_FOUND',
    MAAREFATI_NOT_ELIGIBLE: 'ZAD.UNPAID.MAAREFATI_NOT_ELIGIBLE',
    USER_NOT_FOUND: 'ZAD.COMMON.NOT_FOUND',
    CUSTOMER_NOT_FOUND: 'ZAD.CUSTOMERS.NOT_FOUND',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
    VALIDATION_ERROR: 'ZAD.SALES.VALIDATION_ERROR',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return {i18nId: err.status === 403 ? 'ZAD.UNPAID.FORBIDDEN' : '', fallback: err.error}
}

export function reminderBucket(scheduledAt?: string, status?: string, now = new Date()): 'today' | 'overdue' | 'upcoming' | 'done' {
  if (status && status !== 'PENDING') return 'done'
  if (!scheduledAt) return 'upcoming'
  const date = new Date(scheduledAt)
  if (Number.isNaN(date.getTime())) return 'upcoming'
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const due = ymd(date)
  const today = ymd(now)
  if (due === today) return 'today'
  if (date.getTime() < now.getTime()) return 'overdue'
  return 'upcoming'
}

export function linkedTicketCount(dossier: ZadUnpaidFollowupDossier): number {
  return (dossier.linkedSaleFinancials || dossier.linkedSales || []).length
}

export function lastActivityAt(dossier: ZadUnpaidFollowupDossier): string | null {
  const stamps = [
    dossier.updatedAt,
    ...(dossier.contacts || []).map((c) => c.contactAt || c.createdAt),
    ...(dossier.promises || []).map((p) => p.createdAt),
    ...(dossier.reminders || []).map((r) => r.createdAt || r.scheduledAt),
  ].filter(Boolean) as string[]
  if (!stamps.length) return dossier.createdAt || null
  return stamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
}

export function candidatePhone(_row: ZadUnpaidSaleFinancial): string {
  return '—'
}
